import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CustomTooltip from "./CustomTooltip";
import useZoomableChart from "../sessions/detail/useZoomableChart";
import { formatUTCTimestamp } from "../../utils/DateFormatter";
import { radiansToDegrees, isRadianMetric } from "../../utils/AngleConverter";
import { Container, Spinner, Stack } from "react-bootstrap";
import { HiMagnifyingGlassMinus } from "react-icons/hi2";
import { FaDownload } from "react-icons/fa";
import { useState, useEffect, useMemo } from "react";

interface AreaChartComponentProps {
  fileId: string;
  sensorId: string;
  xKey: string;
  yKey: string[];
  plotData?: any[];
  onDownload?: () => void;
}

export default function AreaChartComponent(
  this: any,
  props: AreaChartComponentProps
) {
  // State to track which areas are visible
  const [visibleAreas, setVisibleAreas] = useState<{ [key: string]: boolean }>({});

  // Initialize all areas as visible when component mounts or yKey changes
  useEffect(() => {
    const initialVisibility: { [key: string]: boolean } = {};
    props.yKey.forEach(key => {
      initialVisibility[key] = true;
    });
    setVisibleAreas(initialVisibility);
  }, [props.yKey]);

  const {
    plotData: originalPlotData,
    handleMouseDown,
    handleMouseMove,
    zoom,
    resetZoom,
    refAreaLeft,
    refAreaRight,
    isLoading,
  } = useZoomableChart(props.fileId, props.sensorId, props.xKey, props.yKey, props.plotData);

  // Convert radian values to degrees for display
  const plotData = useMemo(() => {
    if (!originalPlotData) return originalPlotData;
    
    // Check if any of the yKeys are radian metrics
    const hasRadianMetrics = props.yKey.some(key => isRadianMetric(key));
    if (!hasRadianMetrics) return originalPlotData;
    
    // Convert radian values to degrees
    return originalPlotData.map((item: any) => {
      const newItem = { ...item };
      props.yKey.forEach(key => {
        if (isRadianMetric(key) && key in newItem && typeof newItem[key] === 'number') {
          // Create a new key with _deg suffix containing the converted value
          const degKey = key.replace('_rad', '_deg');
          newItem[degKey] = radiansToDegrees(newItem[key]);
        }
      });
      return newItem;
    });
  }, [originalPlotData, props.yKey]);

  // Handle legend click to toggle area visibility
  const handleLegendClick = (dataKey: string) => {
    setVisibleAreas(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  // Get color for a specific data key
  const getAreaColor = (key: string, index: number) => {
    const colors = ["#FF3282", "#70349D", "#B26691", "#8A5FA8"];
    // Find the original index of the key in props.yKey
    const originalIndex = props.yKey.findIndex(k => k === key);
    return colors[originalIndex >= 0 && originalIndex < colors.length ? originalIndex : index % colors.length];
  };

  // Create gradient definitions for each data key
  const renderGradients = () => {
    return props.yKey.map((key, index) => {
      const color = getAreaColor(key, index);
      const gradientId = `color${key.replace(/[^a-zA-Z0-9]/g, '')}`;
      const areaGradientId = `area${key.replace(/[^a-zA-Z0-9]/g, '')}`;
      
      return (
        <defs key={index}>
          <linearGradient id={gradientId} x1="0%" y1="0" x2="100%" y2="0">
            <stop stopColor={color} />
            <stop offset="0.5" stopColor={color} stopOpacity="0.8" />
            <stop offset="1" stopColor={color} stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id={areaGradientId} x1="0" y1="0" x2="100%" y2="0">
            <stop stopColor={color} stopOpacity="0.4" />
            <stop offset="1" stopColor={color} stopOpacity="0.1" />
          </linearGradient>
        </defs>
      );
    });
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Stack
        gap={2}
        direction="horizontal"
        className="justify-content-end pe-4"
      >
        <Spinner variant="light" size="sm" hidden={!isLoading} />
        <HiMagnifyingGlassMinus
          onClick={resetZoom}
          style={{ cursor: "pointer" }}
        />
        {props.onDownload && (
          <FaDownload
            onClick={props.onDownload}
            style={{ cursor: "pointer", marginLeft: "8px" }}
          />
        )}
      </Stack>
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '5px',
            padding: '10px'
          }}
        >
          <Spinner animation="border" variant="light" />
        </div>
      )}
      <div
        style={{ height: "calc(100% - 40px)", width: "100%", position: "relative" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={plotData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
            onMouseDown={handleMouseDown.bind(this)}
            onMouseMove={handleMouseMove.bind(this)}
            onMouseUp={zoom.bind(this)}
          >
            {renderGradients()}
            <CartesianGrid strokeDasharray="0" vertical={false} opacity={0.2} />
            <XAxis
              dataKey={props.xKey}
              className="chart-label"
              stroke="#E0E0E0"
              dy={10}
              tick={{ fill: "#7E7E7E" }}
              tickFormatter={(value) => {
                if (value.includes("T")) {
                  return formatUTCTimestamp(value);
                }
                return value;
              }}
            />
            <YAxis
              className="chart-label"
              axisLine={false}
              tickLine={false}
              dx={-10}
              tickFormatter={(value) => {
                // Check if any of the displayed areas are angle metrics
                const hasVisibleRadianMetrics = props.yKey.some(key => 
                  isRadianMetric(key) && visibleAreas[key]
                );
                if (hasVisibleRadianMetrics) {
                  return `${value.toFixed(0)}°`;
                }
                return value;
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              onClick={(e) => {
                if (e.dataKey) {
                  handleLegendClick(String(e.dataKey));
                }
              }}
              wrapperStyle={{ cursor: 'pointer' }}
            />
            {props.yKey.map((key, index) => {
              // Use converted key for radian metrics
              const dataKey = isRadianMetric(key) ? key.replace('_rad', '_deg') : key;
              
              // Comprehensive display name mapping
              let displayName = key;
              
              // Handle radian metrics (converted to degrees)
              if (isRadianMetric(key)) {
                displayName = key
                  .replace('apparentWindPitchAngleOfAttack_rad', 'Apparent Wind Pitch Angle (°)')
                  .replace('apparentWindYawAngleOfAttack_rad', 'Apparent Wind Yaw Angle (°)')
                  .replace('ambientWindPitchAngleOfAttack_rad', 'Ambient Wind Pitch Angle (°)')
                  .replace('ambientWindYawAngleOfAttack_rad', 'Ambient Wind Yaw Angle (°)')
                  .replace('motionPitchAngleS_rad', 'Pitch Angle (°)')
                  .replace('motionRollAngleS_rad', 'Roll Angle (°)')
                  .replace('_rad', ' (°)');
              } else {
                // Handle all other metrics with proper units
                displayName = key
                  // Speed metrics
                  .replace('trajectorySpeed_ms', 'Trajectory Speed (m/s)')
                  .replace('apparentWindSpeedMagnitude_ms', 'Apparent Wind Speed (m/s)')
                  .replace('ambientWindSpeedMagnitude_ms', 'Ambient Wind Speed (m/s)')
                  
                  // Environmental metrics
                  .replace('temp', 'Temperature (°C)')
                  .replace('hum', 'Humidity (%)')
                  
                  // Motion metrics
                  .replace('accel_x', 'Accelerometer X (ms²)')
                  .replace('accel_y', 'Accelerometer Y (ms²)')
                  .replace('accel_z', 'Accelerometer Z (ms²)')
                  .replace('gyro_x', 'Gyroscope X')
                  .replace('gyro_y', 'Gyroscope Y')
                  .replace('gyro_z', 'Gyroscope Z')
                  .replace('angle_x', 'Angle X')
                  .replace('angle_y', 'Angle Y')
                  .replace('angle_z', 'Angle Z')
                  .replace('angle_w', 'Angle W')
                  
                  // Position metrics
                  .replace('lat', 'Latitude')
                  .replace('lon', 'Longitude')
                  .replace('alt', 'Altitude')
                  
                  // Other metrics
                  .replace('Tu', 'Turbulence Level')
                  .replace('unev', 'Unevenness Score')
                  .replace('gyro_stab', 'Stability Score')
                  .replace('chi', 'Chi')
                  
                  // Legacy mappings
                  .replace('alpha_deg', 'Alpha (°)')
                  .replace('beta_deg', 'Beta (°)')
                  .replace('v_speed', 'Wind Speed')
                  .replace('speed', 'Speed');
              }
              
              return (
                <Area
                  key={index}
                  type="monotone"
                  dataKey={dataKey}
                  stroke={`url(#color${key.replace(/[^a-zA-Z0-9]/g, '')})`}
                  fill={`url(#area${key.replace(/[^a-zA-Z0-9]/g, '')})`}
                  strokeWidth={3}
                  name={displayName}
                  hide={!visibleAreas[key]}
                />
              );
            })}
            {refAreaLeft && refAreaRight ? (
              <ReferenceArea
                x1={refAreaLeft}
                x2={refAreaRight}
                strokeOpacity={0.3}
              />
            ) : null}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
